import React, { useEffect, useState } from "react";
import {
  TableContainer,
  TableRow,
  TableCell,
  CircularProgress,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { isMobile } from "react-device-detect";

import { searchService } from "../../services/SearchService";
import { strings } from "../../Strings";
import { useSnackbarContext } from "../snackbar/SnackbarContext";

/**
 * 検索テーブルフッターコンポーネント
 * @param {number} page 現在のページ番号
 * @param {number} total 総件数
 * @param {number} from 表示開始件数
 * @param {number} to 表示終了件数
 * @param {number} perPage 1ページ当たりの件数
 * @param {Function} onPageChange ページ変更時の処理
 * @param {Function} onRowsPerPageChange 1ページ当たりの件数変更時の処理
 * @class SearchTableFooter
 */
const SearchTableFooter = ({
  page,
  total,
  from,
  to,
  perPage,
  onPageChange,
  onRowsPerPageChange,
}) => (
  <div className={`search-table-footer ${isMobile ? "mobile" : ""}`}>
    <div className="pager-nav">
      <button className="pager-btn" type="button" aria-label="最初のページ">
        <FirstPageIcon />
      </button>
      <button className="pager-btn" type="button" aria-label="前のページ">
        <NavigateBeforeIcon />
      </button>
      <span className="pager-current">1</span>
      <button className="pager-btn" type="button" aria-label="次のページ">
        <NavigateNextIcon />
      </button>
      <button className="pager-btn" type="button" aria-label="最後のページ">
        <LastPageIcon />
      </button>
    </div>
    <div className="search-pager-text">{`${total}件中${from}～${to}件を表示`}</div>
    <select
      name="perPage"
      value={perPage}
      onChange={onRowsPerPageChange}
      className="search-pager-select"
      aria-label="表示件数"
    >
      {strings.PER_PAGE_OPTIONS.map((option, i) => (
        <option key={i} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * 検索用テーブルコンポーネント
 * @param {array} header テーブル項目
 * @param {object} search 検索値
 * @param {object} defaultSearch デフォルトの検索値
 * @param {string} path 検索パス
 * @param {Node} children 検索パス
 * @param {Function} setData データ取得時の処理
 * @param {object} initOrderBy 初期データソート順
 * @param {number} initPerPage 初期ページ件数
 * @class SearchTable
 */
export default function SearchTable({
  header = [],
  search = null,
  defaultSearch = null,
  path,
  children,
  setData,
  initOrderBy = {},
  initPerPage = null,
  data = [],
}) {
  const searchTablePerPage = searchService.searchTablePerPage;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(initPerPage ?? searchTablePerPage);
  const [isLoading, setLoading] = useState(false);
  const [to, setTo] = useState(0);
  const [from, setFrom] = useState(0);
  const { showSnackbar } = useSnackbarContext();

  /**
   * ページ数、データ表示件数、ソート順が更新されたら再検索する
   * @memberof SearchTable
   */
  useEffect(() => {
    setLoading(true);
  }, [page, perPage]);
  /**
   * 検索条件が更新されたら、ページ数を1にして再検索する
   * @memberof SearchTable
   */
  useEffect(() => {
    setPage(1);
    setLoading(true);
  }, [search]);
  /**
   * 読み込みフラグが true になったらデータを取得する
   * @memberof SearchTable
   */
  useEffect(() => {
    if (!path) setLoading(false);
    if (isLoading && path) {
      const onSearch = async () => {
        try {
          const searchTable = document.querySelector(".search-table");
          if (searchTable) searchTable.scrollTo({ top: 0 });
          const result = (
            await searchService.search(
              path,
              { ...search, ...defaultSearch },
              page,
              perPage,
            )
          ).data;
          setData(result.body.data);
          setTo(result.body.to ?? 0);
          setFrom(result.body.from ?? 0);
          setTotal(result.body.total ?? 0);
          setLoading(false);
        } catch (error) {
          console.error(error);
          showSnackbar("error", error?.data?.message ?? "エラーが発生しました");
          setLoading(false);
        }
      };
      onSearch();
    }
  }, [
    isLoading,
    path,
    defaultSearch,
    page,
    perPage,
    search,
    setData,
    showSnackbar,
  ]);
  /**
   * ページ数が更新された際の処理
   * search-table のスクロール位置をトップに移動する
   * @param {any} _
   * @param {number} page ページ数
   * @memberof SearchTable
   */
  const onPageChange = (_, page) => {
    setPage(page);
  };
  /**
   * 1ページ当たりの表示件数が変更された際の処理
   * @param {any} event イベント
   * @memberof SearchTable
   */
  const onRowsPerPageChange = (event) => {
    setPage(1);
    setPerPage(Number(event.target.value));
    searchService.setSeachTablePerPage(event.target.value);
  };
  return (
    <>
      <TableContainer className="search-table">
        <table>
          <thead>
            <tr>
              {header.map((headCell, index) => (
                <th
                  key={`tableHeaderCell${index}`}
                  className={headCell?.className}
                >
                  {headCell.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={header.length} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={header.length} align="center">
                  データがありません
                </TableCell>
              </TableRow>
            )}
            {children}
          </tbody>
        </table>
      </TableContainer>
      <SearchTableFooter
        page={page}
        total={total}
        to={to}
        from={from}
        perPage={perPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
}
